import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Eye, Send, Users, ImageIcon, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { CampaignHistory } from "@/components/campaign/CampaignHistory";
import { FarmanalizaClicks } from "@/components/campaign/FarmanalizaClicks";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DEFAULT_SUBJECT = "Te presento Farmanaliza, de Archiduque Consulting";
const LINK = "https://www.farmanaliza.com/informacion";
const DEFAULT_BODY = `Hola {farmacia}:

Si FarmaClass te ha resultado útil, entonces no puedes dejar de ver lo siguiente:

Te presento Farmanaliza, una herramienta de Archiduque Consulting para mejorar la gestión de tu farmacia: stocks, evolución de ventas, cuadro de mando e incentivos al equipo.

Todo ello con un coste diez veces menor que otras soluciones del mercado.

Descubre cómo puede ayudarte y reserva tu cita:
👉 ${LINK}

Un saludo,
Pedro Domenech
Archiduque Consulting`;

const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900";

function Progress({ status }) {
  const done = status.status === "done";
  const pct = Math.round(((status.sent + status.failed) / Math.max(1, status.recipients_count)) * 100);
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid="campaign-progress">
      <div className="flex items-center gap-2 text-sm font-semibold">
        {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Loader2 className="h-4 w-4 animate-spin text-sky-600" />}
        {done ? "Envío terminado" : "Enviando…"}
      </div>
      <div className="h-2 bg-slate-100 rounded mt-3 overflow-hidden"><div className="h-full bg-sky-600 transition-[width]" style={{ width: `${pct}%` }} /></div>
      <div className="text-xs text-slate-600 mt-2" data-testid="campaign-progress-text">
        {status.sent} enviados · {status.failed} fallidos · de {status.recipients_count}
      </div>
    </div>
  );
}

export default function AdminCampaignPage() {
  const { user } = useAuth();
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [withImage, setWithImage] = useState(true);
  const [testEmail, setTestEmail] = useState(user?.email && !user.email.endsWith(".local") ? user.email : "pedro@archiduquecarlos.com");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [campaign, setCampaign] = useState(null);

  const payload = (extra) => ({
    subject, body,
    image_url: withImage ? `${window.location.origin}/farmanaliza-banner.jpg` : null,
    image_link: withImage ? LINK : null,
    ...extra,
  });

  const run = async (key, extra, onOk) => {
    setBusy(key);
    try { const { data } = await api.post("/admin/campaign/send", payload(extra)); onOk(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo completar"); }
    finally { setBusy(""); }
  };

  const doPreview = () => run("preview", { scope: "active_users", dry_run: true }, setPreview);
  const doTest = () => run("test", { scope: "test", test_email: testEmail }, () => toast.success(`Prueba enviada a ${testEmail}`));
  const doSend = () => { setConfirm(false); run("send", { scope: "active_users" }, (d) => setCampaign({ id: d.campaign_id, status: "running", sent: 0, failed: 0, recipients_count: d.recipients_count })); };

  useEffect(() => {
    if (!campaign?.id || campaign.status === "done") return undefined;
    const t = setInterval(() => api.get(`/admin/campaign/${campaign.id}`).then((r) => setCampaign({ ...r.data, id: campaign.id })).catch(() => {}), 2500);
    return () => clearInterval(t);
  }, [campaign?.id, campaign?.status]);

  return (
    <div className="max-w-6xl" data-testid="admin-campaign-page">
      <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2"><Mail className="h-7 w-7" /> Correo a clientes</h1>
      <p className="text-slate-600 mt-2 text-sm">Se envía a las farmacias con licencia activa, sin contar a quien se dio de baja. Escribe <code className="bg-slate-100 px-1 rounded">{"{farmacia}"}</code> donde quieras el nombre de cada farmacia.</p>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="space-y-4">
          <label className="block text-sm font-medium">Asunto
            <input className={`${inputCls} mt-1`} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject" />
          </label>
          <label className="block text-sm font-medium">Texto
            <textarea className={`${inputCls} mt-1 h-80 font-mono text-[13px]`} value={body} onChange={(e) => setBody(e.target.value)} data-testid="campaign-body" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={withImage} onChange={(e) => setWithImage(e.target.checked)} data-testid="campaign-with-image" />
            <ImageIcon className="h-4 w-4 text-slate-500" /> Incluir la imagen de Farmanaliza arriba (al pulsarla abre la web)
          </label>
          {withImage && <img src="/farmanaliza-banner.jpg" alt="Farmanaliza" className="rounded-md border border-slate-200 w-64" data-testid="campaign-image-thumb" />}

          <div className="border-t border-slate-200 pt-4 flex flex-wrap items-end gap-2">
            <label className="text-sm font-medium flex-1 min-w-[220px]">Enviarme una prueba a
              <input className={`${inputCls} mt-1`} value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="campaign-test-email" />
            </label>
            <button onClick={doTest} disabled={!!busy} data-testid="campaign-send-test" className="inline-flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
              {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar prueba
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={doPreview} disabled={!!busy} data-testid="campaign-preview" className="inline-flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
              {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Vista previa y destinatarios
            </button>
            <button onClick={() => setConfirm(true)} disabled={!!busy || !preview || campaign?.status === "running"} data-testid="campaign-send-all"
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-40">
              {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} Enviar a todos{preview ? ` (${preview.recipients_count})` : ""}
            </button>
          </div>
          {!preview && <p className="text-xs text-slate-500">Para activar «Enviar a todos», pulsa antes «Vista previa y destinatarios».</p>}
          {campaign && <Progress status={campaign} />}
        </div>

        <div>
          {preview ? (
            <>
              <div className="text-sm mb-2" data-testid="campaign-recipients-count"><strong>{preview.recipients_count}</strong> destinatarios · p. ej. {preview.sample.slice(0, 3).join(", ")}</div>
              <iframe title="Vista previa" srcDoc={preview.preview_html} className="w-full h-[720px] border border-slate-200 rounded-lg bg-white" data-testid="campaign-preview-frame" />
            </>
          ) : (
            <div className="h-full min-h-[300px] grid place-items-center border border-dashed border-slate-300 rounded-lg text-sm text-slate-500">La vista previa del correo aparecerá aquí</div>
          )}
        </div>
      </div>

      <CampaignHistory refreshKey={`${campaign?.id}-${campaign?.status}`} />
      <FarmanalizaClicks />

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="campaign-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Enviar a {preview?.recipients_count} farmacias?</AlertDialogTitle>
            <AlertDialogDescription>El correo «{subject}» llegará a todas las farmacias con licencia activa. No se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="campaign-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doSend} data-testid="campaign-confirm-send">Sí, enviar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
